import { apiClient } from '@/api/client'
import {
  expenseAttachmentPath,
  EXPENSES,
  EXPENSE_ANALYTICS,
  EXPENSE_EXPORT_EXCEL,
  EXPENSE_CATEGORIES,
  ORGANIZATION_EXPENSES,
  UPLOAD,
  companyExpensesPath,
  companyExpensePath,
} from '@/api/endpoints'
import type { ApiResponseWrapper } from '@/types/api.types'
import type { PaginatedResponse } from '@/types/common.types'
import type {
  ExpenseEntry,
  ExpenseAttachment,
  ManagedExpenseCategory,
  CreateExpenseDto,
  UpdateExpenseDto,
  ExpenseListQuery,
  ExpenseWorkspaceQuery,
  ExpenseWorkspaceResponse,
  ExpenseCategoryRecord,
  ExpenseAnalytics,
  ExpenseAnalyticsQuery,
} from '@/features/expenses/types/expense.types'

const EXPENSE_RECEIPT_UPLOAD_CATEGORY = 'expense-receipt'

/** Serializes companyIds[] to the CSV form the API expects, mirroring the bonuses service. */
function withCompanyIds(query: Record<string, unknown>): Record<string, unknown> {
  const { companyIds, ...rest } = query as { companyIds?: string[] } & Record<string, unknown>
  const params: Record<string, unknown> = { ...rest }
  if (Array.isArray(companyIds) && companyIds.length > 0) {
    params.companyIds = companyIds.join(',')
  }
  return params
}

export async function listExpenses(
  companyId: string,
  params: ExpenseListQuery,
): Promise<PaginatedResponse<ExpenseEntry>> {
  const { data } = await apiClient.get<ApiResponseWrapper<PaginatedResponse<ExpenseEntry>>>(
    companyExpensesPath(companyId),
    { params },
  )
  return data.data
}

/** `companyId` null addresses an organization expense (no company). */
const expensesBase = (companyId: string | null): string =>
  companyId ? companyExpensesPath(companyId) : ORGANIZATION_EXPENSES
const expensePath = (companyId: string | null, id: string): string =>
  companyId ? companyExpensePath(companyId, id) : `${ORGANIZATION_EXPENSES}/${id}`

export async function createExpense(
  companyId: string | null,
  body: CreateExpenseDto,
): Promise<ExpenseEntry> {
  const { data } = await apiClient.post<ApiResponseWrapper<ExpenseEntry>>(
    expensesBase(companyId),
    body,
  )
  const doc = data?.data
  if (!doc?._id) throw new Error('Invalid create expense response')
  return doc
}

export async function updateExpense(
  companyId: string | null,
  id: string,
  body: UpdateExpenseDto,
): Promise<ExpenseEntry> {
  const { data } = await apiClient.put<ApiResponseWrapper<ExpenseEntry>>(
    expensePath(companyId, id),
    body,
  )
  const doc = data?.data
  if (!doc?._id) throw new Error('Invalid update expense response')
  return doc
}

export async function deleteExpense(companyId: string | null, id: string): Promise<void> {
  await apiClient.delete(expensePath(companyId, id))
}

/** Operator workspace: cross-company expenses. */
export async function getExpensesWorkspace(
  query?: ExpenseWorkspaceQuery,
): Promise<ExpenseWorkspaceResponse> {
  const params = withCompanyIds({ ...(query ?? {}) } as Record<string, unknown>)
  const { data } = await apiClient.get<ApiResponseWrapper<ExpenseWorkspaceResponse>>(
    EXPENSES,
    { params },
  )
  const payload = data?.data
  if (payload && Array.isArray(payload.data)) return payload
  return { data: [], pagination: { total: 0, page: 1, limit: query?.limit ?? 10 } }
}

/** Downloads matching expenses as an .xlsx blob (same filters as the workspace list). */
export async function exportExpensesExcel(
  query?: ExpenseWorkspaceQuery,
): Promise<Blob> {
  const params = withCompanyIds({ ...(query ?? {}) } as Record<string, unknown>)
  const { data } = await apiClient.get<Blob>(EXPENSE_EXPORT_EXCEL, {
    params,
    responseType: 'blob',
  })
  return data
}

export async function getExpenseAnalytics(
  query: ExpenseAnalyticsQuery,
): Promise<ExpenseAnalytics> {
  const params = withCompanyIds({ ...query } as Record<string, unknown>)
  const { data } = await apiClient.get<ApiResponseWrapper<ExpenseAnalytics>>(
    EXPENSE_ANALYTICS,
    { params },
  )
  const payload = data?.data
  return (
    payload ?? {
      total: 0,
      // No data: leave currency unset so the UI defers to the org's currency.
      currency: '',
      byCategory: [],
      byCompany: [],
      trend: [],
      trendBucket: query.mode === 'year' ? 'month' : 'day',
      trendByCategory: [],
      topCategories: [],
    }
  )
}

/** A receipt or invoice of a ledger expense, read through its access checks. */
export async function fetchExpenseAttachment(expenseId: string, uploadId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(expenseAttachmentPath(expenseId, uploadId), {
    responseType: 'blob',
  })
  return data
}

/** Uploads receipt/invoice files via the generic /upload endpoint and returns attachment refs. */
export async function uploadExpenseAttachments(files: File[]): Promise<ExpenseAttachment[]> {
  const formData = new FormData()
  for (const file of files) formData.append('files', file)
  const { data } = await apiClient.post<
    ApiResponseWrapper<{ data: { files: Array<Record<string, unknown>> } }>
  >(UPLOAD, formData, { params: { category: EXPENSE_RECEIPT_UPLOAD_CATEGORY } })
  const files_ = (data?.data as unknown as { files?: Array<Record<string, unknown>> })?.files
  const list = Array.isArray(files_)
    ? files_
    : ((data?.data?.data?.files as Array<Record<string, unknown>>) ?? [])
  return list.map((f) => ({
    id: String(f.id ?? f._id ?? ''),
    url: String(f.url ?? ''),
    originalName: String(f.originalName ?? ''),
    mimeType: String(f.mimeType ?? ''),
    size: Number(f.size ?? 0),
  }))
}

export async function listExpenseCategories(): Promise<ExpenseCategoryRecord[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<ExpenseCategoryRecord[]>>(
    EXPENSE_CATEGORIES,
  )
  return Array.isArray(data?.data) ? data.data : []
}

export async function createExpenseCategory(name: string): Promise<ExpenseCategoryRecord> {
  const { data } = await apiClient.post<ApiResponseWrapper<ExpenseCategoryRecord>>(
    EXPENSE_CATEGORIES,
    { name },
  )
  const doc = data?.data
  if (!doc?._id) throw new Error('Invalid create category response')
  return doc
}

export async function listManagedExpenseCategories(): Promise<ManagedExpenseCategory[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<ManagedExpenseCategory[]>>(
    '/expenses/category-management',
  )
  return Array.isArray(data?.data) ? data.data : []
}

export async function createManagedExpenseCategory(name: string): Promise<void> {
  await apiClient.post('/expenses/category-management', { name })
}

export async function renameManagedExpenseCategory(id: string, name: string): Promise<void> {
  await apiClient.patch(`/expenses/category-management/${id}`, { name })
}

export async function deleteManagedExpenseCategory(id: string): Promise<void> {
  await apiClient.delete(`/expenses/category-management/${id}`)
}
